import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@gravity-ui/uikit';
import { MKFBrowserModal } from './mkf-browser-modal';
import styles from './mkf-code-selector.module.scss';
import { Xmark } from '@gravity-ui/icons';
import { StructureIcon } from './icons';
import { useGetMKFCodesQuery } from '../../../../../store/api';
import { MKFCode } from './types';

interface MKFCodeSelectorProps {
	prefix?: string[];
	domainCode?: string[];
	onChange: (prefix: string[], domainCode: string[]) => void;
	validationState?: 'invalid' | 'valid';
	errorMessage?: string;
}

export const MKFCodeSelector: React.FC<MKFCodeSelectorProps> = ({ 
	prefix = [], 
	domainCode = [], 
	onChange, 
	validationState, 
	errorMessage 
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedName, setSelectedName] = useState<string>('');
	const { data: mkfCodes = [] } = useGetMKFCodesQuery();

	const fullCode = useMemo(() => {
		const prefixStr = prefix.length > 0 ? prefix[0].toLowerCase() : '';
		const domainStr = domainCode.join('');
		return prefixStr + domainStr;
	}, [prefix, domainCode]);

	const hasSelectedCode = prefix.length > 0 && domainCode.length > 0;

	const findCodeName = (codes: MKFCode[], targetCode: string): string | undefined => {
		for (const code of codes) {
			if (code.code === targetCode) {
				return code.name;
			}
			
			const normalizedCode = code.code.charAt(0).toLowerCase() + code.code.slice(1);
			if (normalizedCode === targetCode) {
				return code.name;
			}
			
			if (code.codes.length > 0) {
				const found = findCodeName(code.codes, targetCode);
				if (found) return found;
			}
		}
		return undefined;
	};

	const currentCodeName = useMemo(() => {
		if (fullCode && mkfCodes.length > 0) {
			return findCodeName(mkfCodes as unknown as MKFCode[], fullCode);
		}
		return undefined;
	}, [fullCode, mkfCodes]);

	useEffect(() => {
		if (currentCodeName && !selectedName) {
			setSelectedName(currentCodeName);
		}
	}, [currentCodeName, selectedName]);

	const handleSelect = (code: string, name?: string) => {
		// Парсим код на prefix и domainCode (всегда сохраняем prefix в нижнем регистре)
		const prefixChar = code.charAt(0).toLowerCase();
		const domainChars = code.slice(1).split('');
		
		onChange([prefixChar], domainChars);
		setSelectedName(name || '');
		setIsModalOpen(false);
	};

	const handleClear = () => {
		onChange([], []);
		setSelectedName('');
	};

	const handleOpenModal = () => {
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	return (
		<div className={styles.container}>
			{hasSelectedCode ? (
				<div className={styles.selectedContainer}>
					<div className={styles.codeContainer}>
						<div className={styles.codeDisplay}>
							<span className={styles.codeValue}>{fullCode}</span>
						</div>
						<button type="button" onClick={handleClear} className={styles.clearButton}>
							<Icon data={Xmark} size={16} />
						</button>
					</div>
					{selectedName && <div className={styles.nameDisplay}>{selectedName}</div>}
				</div>
			) : (
				<button
					type="button"
					onClick={handleOpenModal}
					className={`${styles.selectButton} ${validationState === 'invalid' ? styles.invalid : ''}`}
				>
					<StructureIcon />
					<span>Выбрать код из справочника</span>
				</button>
			)}

			{errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

			<MKFBrowserModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				onSelect={handleSelect}
				selectedCode={fullCode}
			/>
		</div>
	);
};
